import { Box, BoxProps, Text } from '@chakra-ui/react'
import { sanitizeTokenSymbol } from '@/utils/formatUtils'

interface TokenAmountDisplayProps extends BoxProps {
  amount?: string | number | null
  symbol?: string | null
  value?: string | number | null
  fallback?: string
}

const TOKEN_SUFFIX_PATTERN = /^(.+?)\s+([A-Za-z][A-Za-z0-9]{1,15})$/u

const normalizeAmount = (value?: string | number | null): string => {
  if (typeof value === 'number') {
    return Number.isFinite(value)
      ? value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : ''
  }

  return String(value ?? '').trim()
}

const resolveParts = ({
  amount,
  symbol,
  value,
}: Pick<TokenAmountDisplayProps, 'amount' | 'symbol' | 'value'>) => {
  const explicitSymbol = sanitizeTokenSymbol(symbol)

  if (amount !== undefined && amount !== null) {
    return {
      amountText: normalizeAmount(amount),
      tokenSymbol: explicitSymbol,
    }
  }

  const rawValue = normalizeAmount(value)
  const match = rawValue.match(TOKEN_SUFFIX_PATTERN)

  if (match) {
    return {
      amountText: match[1].trim(),
      tokenSymbol: explicitSymbol !== 'UNKNOWN'
        ? explicitSymbol
        : sanitizeTokenSymbol(match[2]),
    }
  }

  return {
    amountText: rawValue,
    tokenSymbol: explicitSymbol,
  }
}

export function TokenAmountDisplay({
  amount,
  symbol,
  value,
  fallback = '—',
  ...boxProps
}: TokenAmountDisplayProps) {
  const { amountText, tokenSymbol } = resolveParts({ amount, symbol, value })
  const displayAmount = amountText || fallback
  const shouldShowSymbol = tokenSymbol && tokenSymbol !== 'UNKNOWN'

  return (
    <Box title={shouldShowSymbol ? `${displayAmount} ${tokenSymbol}` : displayAmount} {...boxProps}>
      <Text fontWeight="medium">
        {displayAmount}
      </Text>
      {shouldShowSymbol && (
        <Text fontSize="xs" color="gray.500">
          {tokenSymbol}
        </Text>
      )}
    </Box>
  )
}
